import {Injectable} from '@angular/core';
import {ToolsService} from '../../../services/tools.service';
import {BookingState} from '../types/booking-state';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import {BookingProviderDetails} from '../types/booking-provider-details';
import {
  AlertStateEnum,
  BookingStepsEnum,
  BookingVirtualServiceGroupIDsEnum,
  StaffMembersSelectorEnum,
  TherapistChoiceEnum,
} from '../enums/booking-enums';
import {CalendarResponse} from '../types/calendar-response';
import {CalendarItem} from '../types/calendar-item';
import {BookingStaffMember} from '../types/booking-staff-member';
import {BookingServiceDetail} from '../../../types/BookingServiceDetail';

@Injectable({
  providedIn: 'any',
})
export class BookingV2EdgeCasesService {
  constructor(private tools: ToolsService) {
    dayjs.locale('sv');
    dayjs.extend(isoWeek);
  }

  private static isCalendarRequestFromCurrentWeek(requestedFirstWeekDay: string): boolean {
    const today = dayjs();
    const selectedDay = dayjs(requestedFirstWeekDay);
    return today.isoWeek() === selectedDay.isoWeek();
  }

  private static isCalendarRequestAfterCurrentWeek(requestedFirstWeekDay: string): boolean {
    const today = dayjs();
    const selectedDay = dayjs(requestedFirstWeekDay);
    return today.isoWeek() < selectedDay.isoWeek();
  }

  private static isStuffMemberSelected(memberId: string, defaultState: BookingState): boolean {
    return !(memberId === defaultState.selectedStaffMemberId || memberId === StaffMembersSelectorEnum.OPTION_ANY_ID);
  }

  public getBookingStateUpdatedWithCalendarEdgeCases(
    response: CalendarResponse,
    requestedFirstWeekDay: string,
    providerDetails: BookingProviderDetails,
    state: BookingState,
    defaultState: BookingState,
    selectedDateTimeStorageFormat: string,
  ): BookingState {
    const nonEmptyItems: Array<CalendarItem> = response.items.filter((item) => item.times.length > 0);
    const newState: BookingState = this.tools.clone(state);
    let showNoTimeSlotsHelpMessageForDate: string;

    // CASE 1: current week, no time slots, next available time is not null, no forced calendar week selection
    // - redirect to next time slot ant set alert status NO_TIMESLOTS_REDIRECTED
    if (
      nonEmptyItems.length === 0 &&
      BookingV2EdgeCasesService.isCalendarRequestFromCurrentWeek(requestedFirstWeekDay) &&
      response.nextAvailableTime !== null &&
      state.forceCalendarDate === defaultState.forceCalendarDate
    ) {
      showNoTimeSlotsHelpMessageForDate = dayjs(response.nextAvailableTime).startOf('week').format('YYYY-MM-DD');
      newState.alert = AlertStateEnum.NO_TIMESLOTS_REDIRECTED;
      const nextAvailableTimeOb = dayjs(response.nextAvailableTime);
      newState.calendarWeekStartDate = nextAvailableTimeOb.startOf('week').format('YYYY-MM-DD');
      newState.calendarWeekEndDate = nextAvailableTimeOb.endOf('week').format('YYYY-MM-DD');
    }
    // CASE 2: current week, no time slots, staff member selected, next available time is null - set alert status NO_TIMESLOTS_FOR_DOCTOR
    if (
      nonEmptyItems.length === 0 &&
      BookingV2EdgeCasesService.isCalendarRequestFromCurrentWeek(requestedFirstWeekDay) &&
      response.nextAvailableTime === null &&
      BookingV2EdgeCasesService.isStuffMemberSelected(state.selectedStaffMemberId, defaultState) === true
    ) {
      newState.alert = AlertStateEnum.NO_TIMESLOTS_FOR_DOCTOR;
    }
    // CASE 3: current week, no time slots, staff member not selected, next available time is null - set alert status NO_TIMESLOTS_AT_ALL
    if (
      nonEmptyItems.length === 0 &&
      BookingV2EdgeCasesService.isCalendarRequestFromCurrentWeek(requestedFirstWeekDay) &&
      response.nextAvailableTime === null &&
      BookingV2EdgeCasesService.isStuffMemberSelected(state.selectedStaffMemberId, defaultState) === false
    ) {
      newState.alert = AlertStateEnum.NO_TIMESLOTS_AT_ALL_30;
    }
    // CASE 4: next week with available slots - set alert NO_TIMESLOTS_REDIRECTED
    if (
      nonEmptyItems.length > 0 &&
      requestedFirstWeekDay === showNoTimeSlotsHelpMessageForDate &&
      BookingV2EdgeCasesService.isCalendarRequestAfterCurrentWeek(requestedFirstWeekDay)
    ) {
      newState.alert = AlertStateEnum.NO_TIMESLOTS_REDIRECTED;
    }
    // CASE 5: staff member mandatory but not selected
    if (
      providerDetails &&
      providerDetails.therapistChoice === TherapistChoiceEnum.REQUIRED &&
      (state.selectedStaffMemberId === defaultState.selectedStaffMemberId ||
        state.selectedStaffMemberId === StaffMembersSelectorEnum.OPTION_NO_SELECTION)
    ) {
      newState.alert = AlertStateEnum.NO_TIMESLOTS_SELECT_DOCTOR;
    }
    // CASE 6: next week, should be available slots but no slots returned from API
    if (
      nonEmptyItems.length === 0 &&
      BookingV2EdgeCasesService.isCalendarRequestAfterCurrentWeek(requestedFirstWeekDay) &&
      state.alert === AlertStateEnum.NO_TIMESLOTS_REDIRECTED
    ) {
      newState.alert = AlertStateEnum.NO_TIMESLOTS_AT_ALL_30;
    }

    // CASE 7: preselected timeslot not exists in slots returned from API
    if (state.timeslot !== null) {
      const timeslotDateOb = dayjs.unix(parseInt(state.timeslot, 10));
      const timeslotFormatted = timeslotDateOb.format(selectedDateTimeStorageFormat);
      const timeslotDay: string = timeslotDateOb.format('YYYY-MM-DD');
      const timeslotTime: string = timeslotDateOb.format('HH:mm');
      if (
        timeslotFormatted === state.selectedDateTime &&
        !this.isTimeslotExistsInCalendar(timeslotDay, timeslotTime, nonEmptyItems)
      ) {
        newState.timeslot = null;
        newState.TimeslotMatchExisted = false;
        newState.selectedDateTime = null;
        newState.step = BookingStepsEnum.CALENDAR;
        newState.alert = AlertStateEnum.NO_TIMESLOTS_NO_MATCH;
      } else if (
        timeslotFormatted === state.selectedDateTime &&
        this.isTimeslotExistsInCalendar(timeslotDay, timeslotTime, nonEmptyItems)
      ) {
        newState.TimeslotMatchExisted = true;
      }
    }

    return newState;
  }

  public getBookingStateUpdatedWithStaffMembersEdgeCases(
    state: BookingState,
    defaultState: BookingState,
    staffMembers: BookingStaffMember[],
  ): BookingState {
    const newState: BookingState = this.tools.clone(state);
    // CASE 1: staffMember selected, but not on current not empty staffMembers list
    if (
      state.selectedStaffMemberId !== defaultState.selectedStaffMemberId &&
      staffMembers.length > 0 &&
      this.tools.isNil(staffMembers.find((member) => member.id === state.selectedStaffMemberId))
    ) {
      newState.selectedStaffMemberId = staffMembers[0].id;
    }

    // CASE 2: staffMember not selected, staffMembers list not empty
    if (state.selectedStaffMemberId === defaultState.selectedStaffMemberId && staffMembers.length > 0) {
      newState.selectedStaffMemberId = staffMembers[0].id;
    }

    // CASE 3: staffMember selected, staffMembers list empty
    if (state.selectedStaffMemberId !== defaultState.selectedStaffMemberId && staffMembers.length === 0) {
      newState.selectedStaffMemberId = defaultState.selectedStaffMemberId;
    }
    return newState;
  }

  public getBookingStateUpdatedWithPreselectedOptions(
    state: BookingState,
    defaultState: BookingState,
    providerDetails: BookingProviderDetails,
    selectedDateTimeStorageFormat: string,
  ): BookingState {
    let newState: BookingState = this.tools.clone(state);
    // CASE 1: selected service linked with only one activity and no booking service selected
    const activitiesLinkedWithService: BookingServiceDetail[] = this.tools.clone(
      this.filterActivitiesByLinkedService(providerDetails.activities, state.serviceId),
    );
    if (
      providerDetails.activities.length === 1 &&
      state.serviceId !== defaultState.serviceId &&
      state.selectedBookingServiceId === defaultState.selectedBookingServiceId &&
      state.timeslot === defaultState.timeslot &&
      activitiesLinkedWithService.length === 1
    ) {
      newState.selectedBookingServiceId = activitiesLinkedWithService[0].id;
      newState.twoFactorAuthenticationRequired = activitiesLinkedWithService[0].twoFactorAuthenticationRequired;
      newState.serviceGroup = state.serviceId;
      newState = this.getBookingStateUpdatedWithStaffMembersEdgeCases(newState, defaultState, providerDetails.staff);
      newState.step = BookingStepsEnum.CALENDAR;
      newState.showSummary = true;
    }

    // CASE 2: selected timeslot with only one service on the list and staff member not mandatory and no calendar date selected
    if (
      providerDetails.activities.length === 1 &&
      state.timeslot !== defaultState.timeslot &&
      providerDetails.therapistChoice !== TherapistChoiceEnum.REQUIRED &&
      state.selectedDateTime === defaultState.selectedDateTime
    ) {
      newState.selectedBookingServiceId = providerDetails.activities[0].id;
      newState.twoFactorAuthenticationRequired = providerDetails.activities[0].twoFactorAuthenticationRequired;
      newState.serviceGroup = this.getFirstGroupIdOrVirtual(providerDetails);
      newState = this.getBookingStateUpdatedWithStaffMembersEdgeCases(newState, defaultState, providerDetails.staff);
      const timeslotDateOb = dayjs.unix(parseInt(state.timeslot, 10));
      newState.selectedDateTime = timeslotDateOb.format(selectedDateTimeStorageFormat);
      newState.step = BookingStepsEnum.SUBMIT;
      newState.showSummary = true;
    }

    // CASE 3: selected timeslot with only one service on the list but staff member is mandatory and no calendar date selected
    if (
      providerDetails.activities.length === 1 &&
      state.timeslot !== defaultState.timeslot &&
      providerDetails.therapistChoice === TherapistChoiceEnum.REQUIRED &&
      state.selectedDateTime === defaultState.selectedDateTime
    ) {
      newState.selectedBookingServiceId = providerDetails.activities[0].id;
      newState.twoFactorAuthenticationRequired = providerDetails.activities[0].twoFactorAuthenticationRequired;
      newState.serviceGroup = this.getFirstGroupIdOrVirtual(providerDetails);
      newState = this.getBookingStateUpdatedWithStaffMembersEdgeCases(newState, defaultState, providerDetails.staff);
      newState.step = BookingStepsEnum.CALENDAR;
      newState.showSummary = true;
    }

    return newState;
  }

  private getFirstGroupIdOrVirtual(providerDetails: BookingProviderDetails): number {
    if (providerDetails.activities[0].links !== null && providerDetails.activities[0].links.length > 0) {
      return providerDetails.activities[0].links[0].careServiceId;
    } else {
      return BookingVirtualServiceGroupIDsEnum.OTHERS;
    }
  }

  private isTimeslotExistsInCalendar(day: string, time: string, calendarItems: Array<CalendarItem>): boolean {
    return !this.tools.isNil(
      calendarItems.find(
        (item) => item.date === day && !this.tools.isNil(item.times.find((timeItem) => timeItem === time)) === true,
      ),
    );
  }

  private filterActivitiesByLinkedService(
    activities: BookingServiceDetail[],
    careServiceId: number,
  ): BookingServiceDetail[] {
    return activities.filter(
      (activity) =>
        activity.links !== null && activity.links.find((link) => link.careServiceId === careServiceId) !== undefined,
    );
  }
}
